import { useEffect, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, initials } from "@/context/AuthContext";
import PageHero from "@/components/PageHero";
import { User, Settings, FileText, LogOut, Bell, Mail, Building2, Trash2 } from "lucide-react";

const LS_ENQUIRIES = "vpcl_enquiries";

export default function Profile() {
  const { user, ready, logout, updateProfile } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    setTab(params.get("tab") || "overview");
  }, [params]);

  useEffect(() => {
    if (!user) return;
    try {
      const all = JSON.parse(localStorage.getItem(LS_ENQUIRIES) || "[]");
      setEnquiries(all.filter((e) => e.userId === user.id));
    } catch { setEnquiries([]); }
  }, [user]);

  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;

  const switchTab = (t) => { setTab(t); setParams({ tab: t }); };

  const removeEnquiry = (id) => {
    const all = JSON.parse(localStorage.getItem(LS_ENQUIRIES) || "[]");
    const next = all.filter((e) => e.id !== id);
    localStorage.setItem(LS_ENQUIRIES, JSON.stringify(next));
    setEnquiries(next.filter((e) => e.userId === user.id));
    toast.success("Enquiry removed.");
  };

  return (
    <div data-testid="profile-page">
      <PageHero
        overline="My Account"
        title={`Welcome, ${user.name.split(" ")[0]}.`}
        subtitle="Manage your VPCL Infra account, review past enquiries and update your preferences."
        image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="profile-hero"
      />

      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="container-corp grid lg:grid-cols-12 gap-10 py-12">
          {/* Sidebar */}
          <aside className="lg:col-span-3" data-testid="profile-sidebar">
            <div className="bg-[#0B2341] text-white p-6 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#16A34A] flex items-center justify-center font-heading text-white text-lg font-semibold">
                  {initials(user.name)}
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-base font-semibold truncate">{user.name}</div>
                  <div className="text-xs text-white/70 truncate">{user.email}</div>
                </div>
              </div>
              {user.company && (
                <div className="mt-4 pt-4 border-t border-white/15 text-xs text-white/70">
                  <span className="text-[#16A34A] uppercase tracking-[0.18em] font-bold text-[10px] block mb-1">Company</span>
                  {user.company}
                </div>
              )}
            </div>

            <nav className="flex lg:flex-col gap-1" data-testid="profile-nav">
              {[
                { k: "overview", l: "Overview", icon: User },
                { k: "enquiries", l: "My Enquiries", icon: FileText },
                { k: "settings", l: "Account Settings", icon: Settings },
              ].map((item) => (
                <button
                  key={item.k}
                  onClick={() => switchTab(item.k)}
                  data-testid={`tab-${item.k}`}
                  className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors text-left ${
                    tab === item.k
                      ? "bg-[#F8F9FA] text-[#0B2341] border-l-2 border-[#16A34A] font-medium"
                      : "text-[#4A5568] hover:bg-[#F8F9FA] border-l-2 border-transparent"
                  }`}
                >
                  <item.icon size={16} />
                  {item.l}
                </button>
              ))}
              <button
                onClick={logout}
                data-testid="profile-logout"
                className="flex items-center gap-3 px-4 py-3 text-sm text-[#16A34A] hover:bg-[#F8F9FA] border-l-2 border-transparent text-left mt-3"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </nav>
          </aside>

          {/* Main */}
          <main className="lg:col-span-9">
            {tab === "overview" && <Overview user={user} enquiries={enquiries} onSwitch={switchTab} />}
            {tab === "enquiries" && <Enquiries enquiries={enquiries} onRemove={removeEnquiry} />}
            {tab === "settings" && <AccountSettings user={user} updateProfile={updateProfile} />}
          </main>
        </div>
      </section>
    </div>
  );
}

function Overview({ user, enquiries, onSwitch }) {
  return (
    <div className="space-y-8" data-testid="profile-overview">
      <div>
        <div className="overline mb-3">Account Overview</div>
        <h2 className="font-heading text-3xl font-semibold text-[#0B2341] leading-tight">Hi {user.name.split(" ")[0]}, here is your snapshot.</h2>
      </div>

      <div className="grid sm:grid-cols-3 gap-px bg-[#E2E8F0]">
        <Stat label="Enquiries" value={enquiries.length} />
        <Stat label="Newsletter" value={user.preferences?.newsletter ? "On" : "Off"} />
        <Stat label="Project Alerts" value={user.preferences?.projectAlerts ? "On" : "Off"} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#F8F9FA] p-6 border border-[#E2E8F0]">
          <FileText className="text-[#16A34A] mb-4" size={24} strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-[#0B2341] mb-2">Submit a project enquiry</h3>
          <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
            Share a tender, EPC scope, open access or captive opportunity with our development desk.
          </p>
          <Link to="/contact" data-testid="goto-contact" className="text-xs uppercase tracking-[0.18em] font-medium text-[#16A34A] hover:underline">
            Go to Contact →
          </Link>
        </div>
        <div className="bg-[#F8F9FA] p-6 border border-[#E2E8F0]">
          <Building2 className="text-[#16A34A] mb-4" size={24} strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-[#0B2341] mb-2">Landowner opportunities</h3>
          <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
            Have land near a substation? Submit details and our landowner desk will revert.
          </p>
          <Link to="/landowner" data-testid="goto-landowner" className="text-xs uppercase tracking-[0.18em] font-medium text-[#16A34A] hover:underline">
            Submit Land Details →
          </Link>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-4">Recent enquiries</h3>
        {enquiries.length === 0 ? (
          <div className="text-sm text-[#4A5568] bg-white border border-dashed border-[#E2E8F0] p-8 text-center">
            No enquiries yet. Submit one from{" "}
            <Link to="/contact" className="text-[#16A34A] hover:underline">Contact</Link> or{" "}
            <Link to="/landowner" className="text-[#16A34A] hover:underline">Landowner</Link>.
          </div>
        ) : (
          <div className="space-y-3">
            {enquiries.slice(0, 3).map((e) => <EnquiryRow key={e.id} e={e} />)}
            {enquiries.length > 3 && (
              <button onClick={() => onSwitch("enquiries")} className="text-xs uppercase tracking-[0.18em] font-medium text-[#16A34A] hover:underline">
                View all enquiries →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Enquiries({ enquiries, onRemove }) {
  return (
    <div className="space-y-6" data-testid="profile-enquiries">
      <div>
        <div className="overline mb-3">Activity</div>
        <h2 className="font-heading text-3xl font-semibold text-[#0B2341] leading-tight">My Enquiries</h2>
      </div>
      {enquiries.length === 0 ? (
        <div className="text-sm text-[#4A5568] bg-white border border-dashed border-[#E2E8F0] p-10 text-center">
          You haven&apos;t submitted any enquiries yet.
          <div className="mt-4">
            <Link to="/contact" className="btn-primary text-xs" data-testid="empty-contact-cta">Submit Project Enquiry</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {enquiries.map((e) => <EnquiryRow key={e.id} e={e} onRemove={onRemove} />)}
        </div>
      )}
    </div>
  );
}

function EnquiryRow({ e, onRemove }) {
  return (
    <div data-testid={`enquiry-row-${e.id}`} className="bg-white border border-[#E2E8F0] p-5 hover:border-[#0B2341]/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#16A34A]">{e.type}</span>
            <span className="text-[10px] text-[#4A5568]">{new Date(e.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="font-heading text-base font-semibold text-[#0B2341] truncate">{e.subject}</div>
          <p className="text-sm text-[#4A5568] mt-1 line-clamp-2">{e.summary}</p>
        </div>
        {onRemove && (
          <button onClick={() => onRemove(e.id)} data-testid={`remove-enquiry-${e.id}`} className="text-[#4A5568] hover:text-[#16A34A] transition-colors p-1" aria-label="Remove">
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function AccountSettings({ user, updateProfile }) {
  const [name, setName] = useState(user.name);
  const [company, setCompany] = useState(user.company || "");
  const [prefs, setPrefs] = useState(user.preferences || { newsletter: true, projectAlerts: true });

  const save = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty."); return; }
    updateProfile({ name: name.trim(), company: company.trim(), preferences: prefs });
    toast.success("Profile updated.");
  };

  return (
    <div className="space-y-8" data-testid="profile-settings">
      <div>
        <div className="overline mb-3">Account</div>
        <h2 className="font-heading text-3xl font-semibold text-[#0B2341] leading-tight">Settings</h2>
      </div>

      <form onSubmit={save} className="space-y-6 bg-white p-8 border border-[#E2E8F0]">
        <div>
          <h3 className="font-heading text-base font-semibold text-[#0B2341] mb-4">Profile</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name">
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="settings-name" />
            </Field>
            <Field label="Email">
              <input value={user.email} disabled className={`${inputCls} bg-[#F8F9FA] text-[#4A5568]`} data-testid="settings-email" />
            </Field>
            <Field label="Company">
              <input value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} data-testid="settings-company" />
            </Field>
          </div>
        </div>

        <div className="pt-6 border-t border-[#E2E8F0]">
          <h3 className="font-heading text-base font-semibold text-[#0B2341] mb-4 flex items-center gap-2">
            <Bell size={16} className="text-[#16A34A]" /> Communication preferences
          </h3>
          <div className="space-y-3">
            <Toggle
              label="Newsletter — monthly updates on tenders, projects and policy."
              checked={prefs.newsletter}
              onChange={(v) => setPrefs({ ...prefs, newsletter: v })}
              testid="pref-newsletter"
            />
            <Toggle
              label="Project alerts — notifications when VPCL Infra commissions a new project."
              checked={prefs.projectAlerts}
              onChange={(v) => setPrefs({ ...prefs, projectAlerts: v })}
              testid="pref-alerts"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" className="btn-primary" data-testid="settings-save">Save Changes</button>
        </div>
      </form>

      <div className="bg-[#F8F9FA] p-6 border border-[#E2E8F0]">
        <div className="flex items-start gap-4">
          <Mail size={20} className="text-[#16A34A] mt-1 flex-shrink-0" />
          <div className="text-sm text-[#4A5568] leading-relaxed">
            <span className="font-medium text-[#0B2341]">Account data:</span> This demo stores your account locally in this browser only. Clearing browser data will remove your account.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white p-6">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5568] font-bold mb-2">{label}</div>
      <div className="font-heading text-3xl font-semibold text-[#0B2341]">{value}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${checked ? "bg-[#16A34A]" : "bg-[#E2E8F0]"}`}
        aria-pressed={checked}
      >
        <span className={`absolute top-0.5 ${checked ? "left-5" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
      </button>
      <span className="text-sm text-[#0A1118] leading-relaxed">{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#0B2341] uppercase tracking-[0.14em] mb-2">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none rounded-none px-4 py-3 text-sm text-[#0A1118]";
